import { useState, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { MilestoneCelebration, useMilestoneChecker } from '../components/Gamification';
import { getMoodById } from '../utils/beeJourney';
import { BadgePill, LocationIcon, WorkIcon } from '../components/icons';
import { getPublicBadges } from '../utils/badges';
import { compatLabel } from '../utils/matching';
import { getDaysUntilExpiry } from '../utils/icebreakers';
import ProfileSheet from '../components/ProfileSheet';
import FoundingBadge from '../components/FoundingBadge';
import { ReferralSuggestions } from './Messages';

// ── Photo with initial fallback ───────────────────────────────────────────
function Avatar({ user, size = 48, radius = 'var(--r-sm)' }) {
  const photo  = (user?.photos || [])[0];
  const initial = (user?.name || '?')[0].toUpperCase();
  return (
    <div style={{ width:size, height:size, borderRadius:radius, overflow:'hidden', flexShrink:0, border:'1px solid var(--gold-border)', background:'var(--dark)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      {photo
        ? <img src={photo} alt={user.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}/>
        : null}
      <span style={{ display: photo ? 'none' : 'flex', fontFamily:"'Cormorant Garamond',serif", fontSize:size*0.4, color:'var(--gold)', alignItems:'center', justifyContent:'center', width:'100%', height:'100%' }}>{initial}</span>
    </div>
  );
}


// ── Main Matches page ───────────────────────────────────────
// Icebreakers keyed by who made the first move
const RELANCE_FR = {
  // You liked them first, they liked back — you didn't follow through
  initiated: [
    'J\'avais liké ton profil en premier — et tu avais liké en retour. J\'aurais dû écrire dès le début.',
    'J\'ai dit oui à ton profil, tu as dit oui au mien. Il reste juste à se dire bonjour.',
    'C\'est moi qui t\'avais remarqué(e) — autant assumer et écrire le premier message.',
  ],
  // They liked you first, you accepted — they took the risk, you went silent
  received: [
    'Tu avais remarqué mon profil en premier — j\'aurais dû te répondre plus tôt.',
    'Tu as fait le premier pas. C\'est à moi de continuer.',
    'Tu m\'as liké(e) et j\'ai accepté — une conversation devrait suivre logiquement.',
  ],
  // Fallback
  mutual: [
    'On s\'est tous les deux dit oui — quelqu\'un doit bien se lancer !',
    'On ne s\'est pas encore parlé. C\'est dommage, j\'aimerais vraiment mieux te connaître.',
    'Je ne voulais pas laisser passer ça sans au moins dire bonjour.',
  ],
};
const RELANCE_EN = {
  initiated: [
    'I liked your profile first, and you liked back — I should have written sooner.',
    'I said yes to your profile, you said yes to mine. We just need to say hi.',
    'I made the first move — I should follow through.',
  ],
  received: [
    'You noticed me first — I should have replied sooner.',
    'You took the leap. It\'s on me to keep it going.',
    'You liked me and I matched back — a conversation should follow.',
  ],
  mutual: [
    'We both said yes — someone has to go first!',
    'We haven\'t talked yet. I\'d love to get to know you better.',
    'Didn\'t want to let this pass without at least saying hi.',
  ],
};

function BeeBackBanner({ beeBackMatches, lang, onOpen }) {
  if (!beeBackMatches?.length) return null;
  return (
    <div style={{ margin:'0 0 1rem', padding:'0.75rem 1rem', background:'rgba(78,201,148,0.06)', border:'1px solid rgba(78,201,148,0.28)', borderRadius:'var(--r-md)' }}>
      <p style={{ fontSize:'0.58rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'#4EC994', marginBottom:'0.5rem' }}>
        ✦ {lang === 'fr' ? 'De retour dans la ruche' : 'Back in the hive'}
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
        {beeBackMatches.slice(0, 3).map(m => (
          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
            <div style={{ width:34, height:34, borderRadius:'50%', overflow:'hidden', background:'var(--charcoal)', flexShrink:0, border:'1px solid rgba(78,201,148,0.35)' }}>
              {m.photos?.[0]
                ? <img src={m.photos[0]} alt={m.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Cormorant Garamond',serif", fontSize:'0.9rem', color:'#4EC994', opacity:0.6 }}>{(m.name||'?')[0]}</div>}
            </div>
            <p style={{ flex:1, fontSize:'0.78rem', color:'var(--fg)', margin:0, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
              <strong>{m.name?.split(' ')[0]}</strong>
              <FoundingBadge user={m} size="xs" lang={lang}/>
              <span style={{ color:'var(--muted)' }}>{lang === 'fr' ? 'est de retour — dites bonjour !' : 'is back — say hi!'}</span>
            </p>
            <button
              onClick={() => onOpen(m)}
              style={{ fontSize:'0.65rem', padding:'0.3rem 0.7rem', background:'rgba(78,201,148,0.1)', border:'1px solid rgba(78,201,148,0.3)', borderRadius:99, color:'#4EC994', cursor:'pointer', fontFamily:'Outfit', whiteSpace:'nowrap' }}
            >
              {lang === 'fr' ? 'Écrire' : 'Write'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DormantMatchNudge({ dormant, lang, currentUserId, onOpen, onDismiss }) {
  if (!dormant?.length) return null;
  const match = dormant[0];

  // Pick icebreaker pool based on who initiated the match
  const situation = match.initiated_by === currentUserId ? 'initiated'
    : match.initiated_by ? 'received'
    : 'mutual';
  const pool = (lang === 'fr' ? RELANCE_FR : RELANCE_EN)[situation];
  const hint = pool[Math.floor(Date.now() / 86400000) % pool.length];

  const situationLabel = {
    fr: {
      initiated: 'Tu avais liké en premier',
      received:  'Ils ont fait le premier pas',
      mutual:    'Match silencieux',
    },
    en: {
      initiated: 'You liked first',
      received:  'They made the first move',
      mutual:    'Silent match',
    },
  }[lang][situation];

  const daysLeft = 14 - Math.floor((Date.now() - new Date(match.matched_at || match.created_at || Date.now()).getTime()) / 86400000);

  return (
    <div style={{ margin:'0 0 1.2rem', padding:'0.9rem 1rem', background:'rgba(224,112,112,0.06)', border:'1px solid rgba(224,112,112,0.25)', borderRadius:'var(--r-md)', position:'relative' }}>
      <button onClick={onDismiss} style={{ position:'absolute', top:'0.5rem', right:'0.5rem', background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'0.9rem', lineHeight:1, padding:'0.2rem' }}>×</button>
      <p style={{ fontSize:'0.58rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'#E07070', marginBottom:'0.5rem' }}>
        ⏳ {situationLabel}
      </p>
      <div style={{ display:'flex', alignItems:'center', gap:'0.7rem', marginBottom:'0.6rem' }}>
        <div style={{ width:38, height:38, borderRadius:'50%', overflow:'hidden', background:'var(--charcoal)', flexShrink:0 }}>
          {match.photos?.[0]
            ? <img src={match.photos[0]} alt={match.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', color:'var(--gold)', opacity:0.5 }}>{(match.name||'?')[0]}</div>}
        </div>
        <div>
          <p style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--fg)', margin:'0 0 2px', display:'flex', alignItems:'center', gap:5 }}>
            <span>{match.name?.split(' ')[0]}</span>
            <FoundingBadge user={match} size="xs" lang={lang}/>
          </p>
          <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
            {lang === 'fr'
              ? `Aucun message depuis votre match — encore ${Math.max(1, daysLeft)}j`
              : `No messages since matching — ${Math.max(1, daysLeft)}d left`}
          </p>
        </div>
      </div>
      <p style={{ fontSize:'0.75rem', color:'var(--fg)', lineHeight:1.55, margin:'0 0 0.65rem', fontStyle:'italic', borderLeft:'2px solid rgba(224,112,112,0.35)', paddingLeft:'0.6rem' }}>"{hint}"</p>
      <button
        onClick={() => onOpen(match)}
        style={{ fontSize:'0.72rem', padding:'0.4rem 0.9rem', background:'rgba(224,112,112,0.1)', border:'1px solid rgba(224,112,112,0.3)', borderRadius:99, color:'#E07070', cursor:'pointer', fontFamily:'Outfit', fontWeight:600 }}
      >
        {lang === 'fr' ? `Écrire à ${match.name?.split(' ')[0]}` : `Write to ${match.name?.split(' ')[0]}`} →
      </button>
    </div>
  );
}

function MatchKPIBar({ networkMatches, conversations, lang }) {
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

  const newThisWeek = networkMatches.filter(m =>
    m.matchedAt && (Date.now() - new Date(m.matchedAt).getTime()) < SEVEN_DAYS
  ).length;

  const withConversation = networkMatches.filter(m =>
    (conversations || []).some(c => c.partner?.id === m.id && c.last_message_at)
  ).length;

  const noMessage = networkMatches.length - withConversation;

  const kpis = [
    {
      value: newThisWeek,
      label: lang === 'fr' ? 'nouveaux cette semaine' : 'new this week',
      color: newThisWeek > 0 ? 'var(--gold)' : 'var(--muted)',
    },
    {
      value: withConversation,
      label: lang === 'fr' ? 'conversations en cours' : 'active chats',
      color: withConversation > 0 ? '#4EC994' : 'var(--muted)',
    },
    {
      value: noMessage,
      label: lang === 'fr' ? 'sans premier message' : 'no message yet',
      color: noMessage > 0 ? '#E07070' : 'var(--muted)',
    },
  ];

  if (networkMatches.length === 0) return null;

  return (
    <div style={{ marginBottom:'1.5rem', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', overflow:'hidden', background:'var(--gold-pale)' }}>
      <div style={{ display:'flex', gap:'1px', background:'var(--gold-border)' }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ flex:1, padding:'0.8rem 0.5rem', background:'rgba(201,168,76,0.06)', textAlign:'center' }}>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.7rem', color:k.color, lineHeight:1, margin:'0 0 4px' }}>{k.value}</p>
            <p style={{ fontSize:'0.58rem', color:'var(--muted)', letterSpacing:'0.04em', lineHeight:1.3, margin:0 }}>{k.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Matches() {

  const {
    matches, likes, users, currentUser, lang,
    setActiveChat, setPage, getCompatibility,
    acceptConnection, declineConnection,
    conversations, features, theme,
  } = useApp();
  const tr = useTr();
  const { checkMilestone, pending, clearPending } = useMilestoneChecker();
  const [showDigest, setShowDigest] = useState(true); // kept for compat, unused

  // Check milestones when matches change
  useEffect(() => {
    if (!matches?.length) return;
    if (matches.length === 1) checkMilestone('first_match', { matchId: matches[0]?.id, compatScore: matches[0]?.compatScore });
    const best = Math.max(...matches.map(m => m.compatScore || 0));
    if (best >= 95) checkMilestone('match_95pct', { compatScore: best });
    else if (best >= 90) checkMilestone('match_90pct', { compatScore: best });
  }, [matches?.length]);
  // matches is array of objects from AppContext (each has .id, .name, .photos, etc.)
  const allMatches = (matches || []).filter(m => m && m.id);
  // Pending = requests awaiting my acceptance (initiated by the other person)
  const pendingRequests = allMatches.filter(m => m.status === 'pending' && m.initiated_by !== currentUser?.id);
  // Sent = requests I initiated, waiting for other person
  const sentRequests = allMatches.filter(m => m.status === 'pending' && m.initiated_by === currentUser?.id);
  // Accepted matches
  const networkMatches = allMatches.filter(m => !m.status || m.status === 'accepted');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [toast, setToast] = useState(null);
  const [showNudge, setShowNudge] = useState(true);

  // Auto-open profile when navigating from a match notification
  useEffect(() => {
    const id = sessionStorage.getItem('openMatchUserId');
    if (!id || !networkMatches.length) return;
    const match = networkMatches.find(m => m.id === id || m.id === Number(id));
    if (match) {
      sessionStorage.removeItem('openMatchUserId');
      setSelectedMatch(match);
    }
  }, [networkMatches.length]);

  // Matches who recently came back from pause (came_back_at within 48h)
  const beeBackMatches = networkMatches.filter(m => {
    if (!m.came_back_at) return false;
    return (Date.now() - new Date(m.came_back_at).getTime()) < 48 * 60 * 60 * 1000;
  });

  // Matches with no conversation message after 14 days
  const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000;
  const dormantMatches = networkMatches.filter(m => {
    const conv = (conversations || []).find(c => c.partner?.id === m.id);
    if (!conv) {
      // No conversation at all — check how old the match is
      const matchedAt = m.matched_at || m.created_at;
      return matchedAt && (Date.now() - new Date(matchedAt).getTime()) > FOURTEEN_DAYS;
    }
    if (!conv.last_message_at) return true;
    return (Date.now() - new Date(conv.last_message_at).getTime()) > FOURTEEN_DAYS;
  });

  const matchedIds = new Set((matches || []).map(m => m.id));
  const likedNotMatched = (users || []).filter(u => likes.has(u.id) && !matchedIds.has(u.id));

  const showToast = (msg, sub) => {
    setToast({ msg, sub });
    setTimeout(() => setToast(null), 3000);
  };

  const openChat = (user) => {
    setActiveChat(user.id);
    setPage('messages');
  };

  const openChatWithNotice = (user) => {
    showToast(
      `Opening conversation with ${user.name.split(' ')[0]}`,
      tr('matches.send_notice')
    );
    setTimeout(() => {
      setActiveChat(user.id);
      setPage('messages');
    }, 1600);
  };

  const openModal = (user) => setSelectedMatch(user);
  const closeModal = () => setSelectedMatch(null);

  return (
    <div style={{ padding:'1rem' }}>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position:'fixed', top:'calc(var(--nav-h) + 12px)', left:'50%', transform:'translateX(-50%)',
          zIndex:500, background:'var(--dark)', border:'1px solid var(--gold-border)',
          padding:'0.75rem 1.2rem', maxWidth:360, width:'calc(100% - 2rem)',
          boxShadow:'0 8px 32px rgba(0,0,0,0.5)', animation:'toastIn .25s ease',
        }}>
          <style>{`@keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(-8px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'0.7rem' }}>
            <span style={{ color:'var(--gold)', fontSize:'0.9rem', flexShrink:0, marginTop:'0.05rem' }}>ℹ</span>
            <div>
              <p style={{ fontSize:'0.82rem', fontWeight:600, marginBottom:'0.15rem' }}>{toast.msg}</p>
              <p style={{ fontSize:'0.72rem', color:'var(--muted)', lineHeight:1.5 }}>{toast.sub}</p>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom:'1.5rem' }}>
        <div className="eyebrow">{tr('matches.connections')}</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:300 }}>
          <em style={{ fontStyle:'italic', color:'var(--gold)' }}>Bee</em> In Contact
        </h2>
      </div>

      <ReferralSuggestions lang={lang}/>
      <MatchKPIBar networkMatches={networkMatches} conversations={conversations} lang={lang}/>
      {showNudge && <DormantMatchNudge dormant={dormantMatches} lang={lang} currentUserId={currentUser?.id} onOpen={(m) => { setActiveChat(m.id); setPage('messages'); }} onDismiss={() => setShowNudge(false)}/>}
      <BeeBackBanner beeBackMatches={beeBackMatches} lang={lang} onOpen={(m) => { setActiveChat(m.id); setPage('messages'); }}/>

      {/* Pending connection requests — requires my acceptance */}
      {pendingRequests.length > 0 && (
        <div style={{ marginBottom:'2rem' }}>
          <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'0.8rem' }}>
            Connection requests — {pendingRequests.length}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:'var(--border)', borderRadius:'var(--r-md)', overflow:'hidden' }}>
            {pendingRequests.map(u => (
              <div key={u.id} style={{ display:'flex', alignItems:'center', gap:'0.9rem', background:'var(--charcoal)', padding:'0.9rem 1rem' }}>
                <Avatar user={u} size={48}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', fontWeight:500, marginBottom:'0.1rem' }}>{u.name}, {u.age}</p>
                  <p style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{u.job} · {u.city}</p>
                </div>
                <div style={{ display:'flex', gap:'0.5rem', flexShrink:0 }}>
                  <button
                    onClick={() => declineConnection(u.matchId)}
                    style={{ padding:'0.45rem 0.8rem', background:'none', border:'1px solid var(--border)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.72rem', cursor:'pointer', borderRadius:'var(--r-sm)' }}
                  >
                    Decline
                  </button>
                  <button
                    onClick={() => acceptConnection(u.matchId)}
                    style={{ padding:'0.45rem 0.8rem', background:'linear-gradient(135deg,#C9A84C,#B8922A)', border:'none', color:'var(--black)', fontFamily:'Outfit', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', borderRadius:'var(--r-sm)' }}
                  >
                    Accept
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent requests — waiting for their acceptance */}
      {sentRequests.length > 0 && (
        <div style={{ marginBottom:'1.5rem' }}>
          <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.6rem' }}>
            Awaiting response — {sentRequests.length}
          </p>
          <div style={{ display:'flex', gap:'0.6rem', flexWrap:'wrap' }}>
            {sentRequests.map(u => (
              <div key={u.id} style={{ display:'flex', alignItems:'center', gap:'0.45rem', background:'var(--dark)', border:'1px solid var(--border)', padding:'0.3rem 0.65rem', borderRadius:'var(--r-pill)' }}>
                <Avatar user={u} size={22} radius="50%"/>
                <span style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{(u.name||'').split(' ')[0]}</span>
                <span style={{ fontSize:'0.6rem', color:'var(--gold)', opacity:0.7 }}>connecting…</span>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* No matches state */}
      {networkMatches.length === 0 && pendingRequests.length === 0 && sentRequests.length === 0 && (
        <div style={{ textAlign:'center', padding:'3rem 1rem', borderBottom:'1px solid var(--border)', marginBottom:'2rem' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.5rem', color:'var(--gold)', marginBottom:'0.8rem' }}>✦</div>
          <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', fontWeight:400, marginBottom:'0.5rem' }}>
            <em className="bee-word">{tr('matches.empty_bee')}</em>
          </h3>
          <p style={{ color:'var(--muted)', fontSize:'0.85rem', marginBottom:'1.2rem' }}>{tr('matches.empty_swipe')}</p>
          <button className="btn btn-outline btn-md" onClick={()=>{ import('../utils/navigate').then(({ navigate }) => navigate('discover')); }}>
            {tr('profile.discover_cta')} →
          </button>
        </div>
      )}

      {/* All matches list */}
      {networkMatches.length > 0 && (
        <div style={{ marginBottom:'2rem' }}>
          <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.8rem' }}>
            {tr('matches.all_title')} — {networkMatches.length}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:'var(--border)' }}>
            {networkMatches.map(u => {
              const compat  = getCompatibility(u.id);
              const pct2    = compat ? Math.round(compat.score) : null;
              const cl      = pct2 != null ? compatLabel(pct2) : null;
              const isNew   = u.matchedAt && (Date.now() - new Date(u.matchedAt).getTime()) < 7 * 24 * 60 * 60 * 1000;
              return (
                <div key={u.id} style={{
                  display:'flex', alignItems:'center', gap:'0.9rem',
                  background: isNew ? 'rgba(201,168,76,0.05)' : 'var(--charcoal)',
                  padding:'0.9rem 1rem',
                  borderLeft: isNew ? '2px solid var(--gold)' : '2px solid transparent',
                  transition:'background 0.15s',
                }}>
                  <Avatar user={u} size={52}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.45rem' }}>
                      <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.05rem', fontWeight:500 }}>{u.name}</span>
                      {isNew && <span style={{ fontSize:'0.55rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)', background:'rgba(201,168,76,0.12)', padding:'1px 5px', borderRadius:99 }}>new</span>}
                      {getPublicBadges(u, {}, lang).slice(0,2).map(b => <BadgePill key={b.id} badge={b} size="sm"/>)}
                    </div>
                    <p style={{ fontSize:'0.78rem', color:'var(--muted)', marginTop:'0.1rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ display:'flex', alignItems:'center', gap:'0.3rem', flexWrap:'wrap' }}>
                        <WorkIcon size={10} color="var(--muted)"/>{u.job}
                        <span style={{opacity:0.4}}>·</span>
                        <LocationIcon size={10} color="var(--muted)"/>{u.city}
                      </span>
                    </p>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginTop:'0.3rem', flexWrap:'wrap' }}>
                      {cl && <span style={{ fontSize:'0.65rem', color:cl.color, fontWeight:600, letterSpacing:'0.04em' }}>{pct2}% match</span>}
                      {(() => {
                        const days = getDaysUntilExpiry(u.matchedAt);
                        if (days <= 3 && days > 0) return <span style={{ fontSize:'0.6rem', color:'#E07070', fontWeight:600 }}>⏱ {days}d left</span>;
                        if (days === 0) return <span style={{ fontSize:'0.6rem', color:'var(--muted)', fontStyle:'italic' }}>{tr('matches.expired')}</span>;
                        return null;
                      })()}
                    </div>
                  </div>
                  {/* Profile icon */}
                  <button
                    onClick={() => openModal(u)}
                    title={lang === 'fr' ? 'Voir le profil' : 'View profile'}
                    style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'50%', width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--muted)', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--mid)'}
                    onMouseLeave={e => e.currentTarget.style.background='var(--dark)'}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                    </svg>
                  </button>
                  {/* Message icon */}
                  <button
                    onClick={() => openChat(u)}
                    title={lang === 'fr' ? 'Écrire' : 'Message'}
                    style={{ background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:'50%', width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--gold)', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(201,168,76,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.background='var(--gold-pale)'}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Liked but not matched */}
      {likedNotMatched.length > 0 && (
        <div>
          <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.8rem' }}>
            Liked · Awaiting match — {likedNotMatched.length}
          </p>
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
            {likedNotMatched.map(u => (
              <div key={u.id} style={{ display:'flex', alignItems:'center', gap:'0.5rem', background:'var(--dark)', border:'1px solid var(--border)', padding:'0.3rem 0.7rem' }}>
                <div style={{ width:24, height:24, borderRadius:'var(--r-sm)', overflow:'hidden' }}>
                  <img src={(u.photos||[])[0]} alt={u.name} style={{ width:'100%', height:'100%', objectFit:'cover', filter:'blur(4px)' }}/>
                </div>
                <span style={{ fontSize:'0.78rem', color:'var(--muted)' }}>{(u.name||'').split(' ')[0]}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize:'0.72rem', color:'var(--muted2)', fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', marginTop:'0.6rem' }}>
            <em className="bee-word">{tr('discover.bee_patient')}</em> — they may like you back ✦
          </p>
        </div>
      )}

      {/* Match detail sheet */}
      {selectedMatch && (
        <ProfileSheet
          user={selectedMatch}
          onClose={closeModal}
          preview={true}
        />
      )}
          {pending && <MilestoneCelebration milestone={pending} onClose={clearPending}/>}
    </div>
  );
}